import { cardById, chequeCards } from '../deck';
import { bidRange, lowestInMarket, activeBidders } from '../actions/auctionActions';

// The computer players.
//
// For Sale's central arithmetic is unusually honest: an unspent coin is a
// scored point (IELLO p7 counts leftover money at face value), so a bid is
// never "spending money to get points" - it is trading points for a house
// whose only value is the cheque it will eventually fetch. Everything below
// prices houses in expected cheque-thousands and compares like with like.
//
// Everything used here is PUBLIC information only, plus the bot's own hand
// and money: the market, who has passed, everyone's current bids, the
// cheques already revealed. Nothing reads the shuffled deck order, and
// nothing reads another player's coins or hidden cheques - the removed
// cards (3-4 player games) stay unknowable, exactly as at a real table.

/**
 * What a house is worth in expected cheque-thousands.
 *
 * Cheques run 0-15 over houses 1-30, so rank-for-rank a house fetches about
 * half its face value. The mapping is genuinely linear-ish (both scales are
 * flat sequences); the fudge is that ranks shift as cards leave the game,
 * which the sweep says is not worth modelling - `spread` below is what
 * actually earns money.
 */
export const worth = (houseValue) => houseValue / 2;

/**
 * The cheques not yet distributed, as values. Includes the current market
 * (they are being distributed right now) and, unavoidably, the cards
 * removed unseen at setup - nobody at the table knows which those are, so
 * treating them as "still out there" is the honest prior, not a bug.
 */
export const chequesStillOut = (state) => {
  const gone = new Set(state.players.flatMap((p) => p.cheques));
  return chequeCards().filter((c) => !gone.has(c.id)).map((c) => c.value);
};

export const PERSONALITIES = {
  // aggression scales how far past "mathematically fair" a bot will chase a
  // house in phase 1. matchCurve bends the phase-2 rank match: below 1
  // spends good houses sooner, above 1 hoards them for the best rounds.
  //
  // These numbers came out of the sweep at the foot of this file, not out of
  // intuition - the intuitive first draft (0.7/1.0/1.35, curves 1.25/1.0/0.8)
  // had Frugal winning 55-67% of mixed tables, because "mathematically fair"
  // in bidActionFor overpays and money is score.
  cautious: { label: 'Frugal', aggression: 0.5, matchCurve: 1.0 },
  steady: { label: 'Fair', aggression: 0.65, matchCurve: 0.85 },
  daring: { label: 'Flashy', aggression: 0.9, matchCurve: 0.55 },
};

const PERSONALITY_ORDER = ['steady', 'cautious', 'daring'];

/**
 * Two bots of the same temperament must never be twins - a table that bids
 * and sells identically is worse than any individual setting (the same
 * lesson the Incan Gold sweep taught). The nudge is per-seat and small.
 */
const SEAT_NUDGE = [0, 0.07, -0.07, 0.12, -0.12, 0.04];

export const tuningFor = (personality, botIndex = 0) => {
  const base = PERSONALITIES[personality] || PERSONALITIES.steady;
  const nudge = SEAT_NUDGE[botIndex % SEAT_NUDGE.length];
  return {
    ...base,
    aggression: base.aggression + nudge,
    matchCurve: Math.max(0.5, base.matchCurve - nudge),
  };
};

export const personalityFor = (index) => PERSONALITY_ORDER[index % PERSONALITY_ORDER.length];

/**
 * Phase 1: bid or pass.
 *
 * The comparison that matters: at a bid of b, winning costs b while passing
 * costs ceil(b/2) - so staying in is worth it while the house you would win
 * beats the house you would get by passing by more than the difference.
 * The bot prices the top of the market against the bottom, caps its total
 * bid at aggression x that gap, and raises minimally while under its cap.
 *
 * Returns { type: 'bid', amount } or { type: 'pass' }.
 */
export const bidActionFor = (state, seat, personality = 'steady') => {
  const player = state.players[seat];
  const { aggression } = tuningFor(personality, player.botIndex ?? seat);

  const { canBid, min, max } = bidRange(state, seat);
  if (!canBid) return { type: 'pass' };

  const values = state.market.map((id) => cardById(id).value);
  const top = Math.max(...values);
  const low = cardById(lowestInMarket(state)).value;

  // With more bidders still in, the pass-out order gives later passers
  // better leftovers, so the real gap to the top narrows a little.
  const crowd = Math.max(1, activeBidders(state).length - 1);
  const gap = (worth(top) - worth(low)) / Math.sqrt(crowd);

  // Winning at bid b costs b - ceil(b/2) ~ b/2 more than passing at b, so
  // the fair total bid is about twice the gap.
  const cap = Math.floor(2 * gap * aggression);

  // Never bid so deep that passing later would hurt more than the house is
  // worth; and never below the table's floor.
  if (min > cap || min > max) return { type: 'pass' };

  // Always the minimal raise. A jump-bid flourish was tried for Flashy and
  // measured (sweep, 2026-08-27): it cost ~3.5 points of win rate at a
  // 4-player table and bought nothing - "confidence" that only ever overpays.
  return { type: 'bid', amount: min };
};

/**
 * Phase 2: which house to sell into this market.
 *
 * Rank-matching: judge how good this round's cheques are against everything
 * still to come, then play the house at the same height in the hand. Best
 * cheques left -> best house; junk round -> junk house. matchCurve bends
 * that (hoard vs spend), and a market with a $0 in it drags the round's
 * quality down exactly as much as it deserves to.
 *
 * Returns a card id from the player's hand.
 */
export const pickFor = (state, player, personality = 'steady') => {
  const { matchCurve } = tuningFor(personality, player.botIndex ?? player.seat ?? 0);
  const hand = [...player.properties].sort((a, b) => cardById(a).value - cardById(b).value);
  if (hand.length <= 1) return hand[0];

  const market = state.market.map((id) => cardById(id).value);
  const still = chequesStillOut(state);
  const marketMean = market.reduce((s, v) => s + v, 0) / market.length;

  // Where does this round sit among the cheques still out there?
  const below = still.filter((v) => v < marketMean).length;
  const q = still.length ? below / still.length : 0.5;

  const bent = Math.pow(q, matchCurve);
  const index = Math.min(hand.length - 1, Math.round(bent * (hand.length - 1)));
  return hand[index];
};

// ---------------------------------------------------------------------------
// What the sweep found (yarn tune-bots, 8000 games per table, 2026-08-27):
//
// - BOTH knobs are strength axes, as in incan-gold - but aggression's is an
//   inverted U, not a slope. Holding curve at 0.9, win% by aggression at a
//   4-player mixed table: 0.25 -> 7.4, 0.4 -> 28.7, 0.55 -> 41.2,
//   0.7 -> 37.9, 0.9 -> 23.4, 1.1 -> 11.8. Passing everything hoards $12k of
//   coins and a hand of junk; chasing every house overpays. The peak sits
//   near 0.55 because a coin never spent is a point never risked.
//
// - matchCurve is monotone: lower is stronger. At aggression 0.6, win% by
//   curve: 0.45 -> 31.8, 0.6 -> 30.5, 0.8 -> 25.2, 1.0 -> 20.9,
//   1.25 -> 16.1. Hoarding your mansions for the perfect round loses to
//   simply spending them - the perfect round mostly never comes, and a
//   mansion left in hand at the end became a $0 cheque somewhere.
//
// - The shipped triple in one mixed field (win% at 3/4/6 players):
//     Frugal 0.5/1.0   40.8 / 24.2 / 11.8   (hoards ~$2.5-6k to the end)
//     Fair   0.65/0.85 34.5 / 29.6 / 21.1
//     Flashy 0.9/0.55  24.5 / 21.3 / 17.2
//   The honest weakness, recorded rather than smoothed over: Frugal's
//   cash-hoarding is genuinely strong at 3 players (fewer auctions to lose)
//   and genuinely weak at 6 (start is $14k, cheques dominate). That is a
//   table-size effect the constants cannot fix, and it reads as personality
//   at the table, which is what it is.
